import React from 'react'
import { motion } from 'framer-motion'
import { useParams, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  ArrowLeft, 
  BarChart3, 
  Settings, 
  MousePointer, 
  AlertTriangle, 
  Smartphone, 
  Zap, 
  Shield, 
  Users,
  Calendar,
  Eye,
  Share2,
  RefreshCw,
  TrendingUp,
  CheckCircle,
  Clock
} from 'lucide-react'

export const WorkForceFeatureDetailPage: React.FC = () => {
  const { featureId } = useParams()
  const { t } = useTranslation('workforce')

  if (!featureId || featureId !== 'workforce') {
    return <Navigate to="/workforce" replace />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <a 
            href="/workforce" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
          </a>
          
          <div className="text-center">
            <div className="inline-flex items-center gap-3 bg-blue-600 text-white px-6 py-3 rounded-full mb-6">
              <Users className="w-6 h-6" />
              <span className="font-semibold">{t('features.mobileWorkforce.title')}</span>
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              {t('features.mobileWorkforce.title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              {t('features.mobileWorkforce.description')}
            </p>
          </div>
        </motion.div>

        {/* Dashboard Principal */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-20"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 bg-blue-50 px-6 py-3 rounded-full mb-6">
                <BarChart3 className="w-6 h-6 text-blue-600" />
                <span className="text-blue-800 font-semibold">{t('taskManagement.dashboard.subtitle')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('taskManagement.dashboard.title')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('taskManagement.dashboard.description')}
              </p>
            </div>
            
            <div className="relative mb-8">
              <img 
                src="/images/workforce/shift_dashboard.png" 
                alt="Dashboard WorkForce - Interface Principal"
                className="w-full rounded-2xl shadow-xl border border-gray-100"
              />
              <div className="absolute top-6 right-6 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 shadow-lg">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                {t('taskManagement.dashboard.monitoring')}
              </div>
              <div className="absolute bottom-6 left-6 bg-black/80 text-white px-4 py-2 rounded-lg text-sm">
                💡 {t('taskManagement.dashboard.realTimeMetrics')}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center border border-blue-200">
                <div className="text-3xl font-bold text-blue-700 mb-2">87%</div>
                <div className="text-sm text-blue-600 font-medium">{t('taskManagement.metrics.completion')}</div>
                <div className="text-xs text-blue-500 mt-1">↗ +5% {t('taskManagement.metrics.vsPrevious')}</div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 text-center border border-green-200">
                <div className="text-3xl font-bold text-green-700 mb-2">124</div>
                <div className="text-sm text-green-600 font-medium">{t('taskManagement.metrics.active')}</div>
                <div className="text-xs text-green-500 mt-1">18 departamentos</div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 text-center border border-purple-200">
                <div className="text-3xl font-bold text-purple-700 mb-2">18</div>
                <div className="text-sm text-purple-600 font-medium">{t('mobileDetail.templates.title')}</div>
                <div className="text-xs text-purple-500 mt-1">6 {t('featuresShowcase.workforce.highlights[2]')}</div>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 text-center border border-orange-200">
                <div className="text-3xl font-bold text-orange-700 mb-2">4</div>
                <div className="text-sm text-orange-600 font-medium">{t('mobileDetail.exceptions.monitoring.critical')}</div>
                <div className="text-xs text-orange-500 mt-1">⚠ {t('featuresShowcase.workforce.highlights[3]')}</div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Seção de Templates */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-20"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 bg-green-50 px-6 py-3 rounded-full mb-6">
                <Settings className="w-6 h-6 text-green-600" />
                <span className="text-green-800 font-semibold">{t('mobileDetail.templates.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('mobileDetail.templates.headline')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('mobileDetail.templates.description')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
              <div>
                <img 
                  src="/images/workforce/shift_templates.png" 
                  alt="Templates de Turnos - Configuração"
                  className="w-full rounded-2xl shadow-xl border border-gray-100"
                />
                <div className="mt-4 text-center text-sm text-gray-600">
                  {t('mobileDetail.templates.config')}
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6 border border-green-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-6 h-6 text-green-600" />
                    {t('mobileDetail.templates.advanced.title')}
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    {(t('mobileDetail.templates.advanced.features', { returnObjects: true }) as string[]).map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                

              </div>
            </div>
          </div>
        </motion.section>

        {/* Seção Planning Interface */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-20"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 bg-purple-50 px-6 py-3 rounded-full mb-6">
                <MousePointer className="w-6 h-6 text-purple-600" />
                <span className="text-purple-800 font-semibold">{t('taskManagement.kanban.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('taskManagement.planning.title')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('taskManagement.planning.description')}
              </p>
            </div>
            
            <div className="relative mb-8">
              <img 
                src="/images/workforce/shift_planning_detail.png" 
                alt="Interface Drag & Drop - Planning de turnos"
                className="w-full rounded-2xl shadow-xl border border-gray-100"
              />
              <div className="absolute top-6 left-6 bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 shadow-lg">
                <MousePointer className="w-4 h-4" />
                {t('taskManagement.kanban.visualControls')}
              </div>
              <div className="absolute bottom-6 right-6 bg-black/80 text-white px-4 py-2 rounded-lg text-sm">
                ⚡ {t('taskManagement.dashboard.realTimeMetrics')}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{t('taskManagement.planning.integration')}</h3>
                <ul className="space-y-3 text-gray-700">
                  {(t('taskManagement.planning.features', { returnObjects: true }) as string[]).map((feature, index) => (
                    <li key={index}>
                      {index === 0 ? '�' : index === 1 ? '📋' : index === 2 ? '�🔄' : '⚡'} <strong>{feature.split(':')[0]}:</strong> {feature.split(':')[1]}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-6 border border-indigo-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{t('taskManagement.technical.title')}</h3>
                <ul className="space-y-3 text-gray-700">
                  {[
                    '🎯 Auto-fill: ' + t('taskManagement.technical.taskEngine.features[0]'),
                    '🛡️ ' + t('taskManagement.technical.taskEngine.features[1]'),
                    '📊 ' + t('taskManagement.technical.analytics.features[0]'),
                    '🏢 ' + t('featuresShowcase.analytics.highlights[0]')
                  ].map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Sistema de Exceções */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-20"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 bg-red-50 px-6 py-3 rounded-full mb-6">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                <span className="text-red-800 font-semibold">{t('mobileDetail.exceptions.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('mobileDetail.exceptions.headline')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('mobileDetail.exceptions.description')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
              <div>
                <img 
                  src="/images/workforce/shift_excecoes.png" 
                  alt="Sistema de Exceções - Dashboard"
                  className="w-full rounded-2xl shadow-xl border border-gray-100"
                />
                <div className="mt-4 text-center text-sm text-gray-600">
                  {t('mobileDetail.exceptions.dashboard')}
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-2xl p-6 border border-red-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Eye className="w-6 h-6 text-red-600" />
                    {t('mobileDetail.exceptions.monitoring.title')}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                      <span className="text-sm font-medium">{t('mobileDetail.exceptions.monitoring.critical')}</span>
                      <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">4</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                      <span className="text-sm font-medium">{t('mobileDetail.exceptions.monitoring.pending')}</span>
                      <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs font-bold">7</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                      <span className="text-sm font-medium">{t('mobileDetail.exceptions.monitoring.resolved')}</span>
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold">12</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Share2 className="w-6 h-6 text-blue-600" />
                    {t('mobileDetail.exceptions.workflow.title')}
                  </h3>
                  <div className="space-y-3">
                    {(t('mobileDetail.exceptions.workflow.steps', { returnObjects: true }) as string[]).map((step, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                        <div className={`w-8 h-8 bg-${index === 0 ? 'blue' : index === 1 ? 'orange' : 'green'}-100 rounded-full flex items-center justify-center`}>
                          <span className={`text-${index === 0 ? 'blue' : index === 1 ? 'orange' : 'green'}-600 text-xs font-bold`}>{index + 1}</span>
                        </div>
                        <span className="text-sm">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Interface Mobile */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="mb-20"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 bg-orange-50 px-6 py-3 rounded-full mb-6">
                <Smartphone className="w-6 h-6 text-orange-600" />
                <span className="text-orange-800 font-semibold">{t('taskManagement.mobile.subtitle')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('taskManagement.mobile.title')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('taskManagement.mobile.description')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-6 border border-orange-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Clock className="w-6 h-6 text-orange-600" />
                    {t('mobileDetail.timeClocking.title')}
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    {(t('mobileDetail.timeClocking.clockIn.features', { returnObjects: true }) as string[]).map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6 border border-green-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <RefreshCw className="w-6 h-6 text-green-600" />
                    {t('mobileDetail.requests.title')}
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    {[
                      t('mobileDetail.requests.shiftExchange.description'),
                      t('mobileDetail.requests.absences.description'),
                      t('mobileDetail.workflow.steps.communicateStatus.description'),
                      t('mobileDetail.mobileInterface.notifications.features[0]')
                    ].map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div>
                <img 
                  src="/images/workforce/mobile-hero.jpg" 
                  alt="Interface Mobile - PWA WorkForce"
                  className="rounded-2xl shadow-xl border border-gray-100"
                />
                <div className="mt-4 text-center text-sm text-gray-600">
                  {t('mobileDetail.pwaSection.features.install')}
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Automação */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="mb-20"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 bg-indigo-50 px-6 py-3 rounded-full mb-6">
                <Zap className="w-6 h-6 text-indigo-600" />
                <span className="text-indigo-800 font-semibold">{t('mobileDetail.automation.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('mobileDetail.automation.headline')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('mobileDetail.automation.description')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
              <div>
                <img 
                  src="/images/workforce/shift_configutarion.png" 
                  alt="Jobs Automatizados - Dashboard"
                  className="w-full rounded-2xl shadow-xl border border-gray-100"
                />
                <div className="mt-4 text-center text-sm text-gray-600">
                  {t('mobileDetail.automation.systemDesc')}
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-2xl p-6 border border-indigo-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Zap className="w-6 h-6 text-indigo-600" />
                    {t('mobileDetail.automation.jobs.title')}
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    {(t('mobileDetail.automation.jobs.features', { returnObjects: true }) as Array<{title: string, desc: string}>).map((feature, index) => {
                      let bgColor = 'bg-indigo-100';
                      let textColor = 'text-indigo-600';
                      let Icon = Calendar;

                      if (index === 1) {
                        bgColor = 'bg-green-100';
                        textColor = 'text-green-600';
                        Icon = RefreshCw;
                      } else if (index === 2) {
                        bgColor = 'bg-purple-100';
                        textColor = 'text-purple-600';
                        Icon = Settings;
                      }

                      return (
                        <li key={index} className="flex items-center gap-3">
                          <div className={`w-8 h-8 ${bgColor} rounded-lg flex items-center justify-center`}>
                            <Icon className={`w-4 h-4 ${textColor}`} />
                          </div>
                          <div>
                            <div className="font-medium">{feature.title}</div>
                            <div className="text-sm text-gray-500">{feature.desc}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Settings className="w-6 h-6 text-blue-600" />
                    {t('mobileDetail.automation.api.title')}
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    {(t('mobileDetail.automation.api.features', { returnObjects: true }) as string[]).map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-blue-600" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Especificações Técnicas */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
          className="mb-20"
        >
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 shadow-2xl text-white">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 bg-white/10 px-6 py-3 rounded-full mb-6">
                <Shield className="w-6 h-6 text-white" />
                <span className="text-white font-semibold">{t('mobileDetail.techSpecs.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">
                {t('mobileDetail.techSpecs.headline')}
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                {t('mobileDetail.techSpecs.description')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                  <RefreshCw className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{t('mobileDetail.techSpecs.websockets.title')}</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  {(t('mobileDetail.techSpecs.websockets.features', { returnObjects: true }) as string[]).map((feature, index) => (
                    <li key={index}>• {feature}</li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{t('mobileDetail.techSpecs.audit.title')}</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  {(t('mobileDetail.techSpecs.audit.features', { returnObjects: true }) as string[]).map((feature, index) => (
                    <li key={index}>• {feature}</li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{t('mobileDetail.techSpecs.performance.title')}</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  {(t('mobileDetail.techSpecs.performance.features', { returnObjects: true }) as string[]).map((feature, index) => (
                    <li key={index}>• {feature}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.section>


      </div>
    </div>
  )
}

export default WorkForceFeatureDetailPage
